import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "../redux/slices/usersSlice";
import AddEditModal from "./AddEditModal";
import { Edit2, Trash2, User as UserIcon, Crown, CheckCircle2, AlertCircle } from "lucide-react";
import { deleteUser } from "../redux/slices/usersSlice";
import axios from "axios";

const UsersList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [userSubscriptions, setUserSubscriptions] = useState({});
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  
  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // Obtener datos de Redux
  const { users, loading: usersLoading, totalCount } = useSelector((state) => state.users);
  const currentUser = useSelector((state) => state.auth.user);

  // Obtener todos los planes de suscripción
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/subscriptionPlans`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          }
        );
        setSubscriptionPlans(response.data);
      } catch (error) {
        console.error("Error al obtener los planes de suscripción:", error);
      }
    };
    
    fetchSubscriptionPlans();
  }, []);

  // Obtener usuarios
  useEffect(() => {
    dispatch(fetchUsers({ page: currentPage, limit: itemsPerPage }))
      .unwrap()
      .then(response => {
        if (response.totalCount) {
          setTotalPages(Math.ceil(response.totalCount / itemsPerPage));
        }
      })
      .catch(error => {
        console.error("Error fetching users:", error);
        setError("Error al cargar los usuarios");
      });
  }, [dispatch, currentPage, itemsPerPage]);
  
  // Obtener suscripciones activas para todos los usuarios
  useEffect(() => {
    const fetchAllUserSubscriptions = async () => {
      if (!users || users.length === 0) return;
      
      setLoadingSubscriptions(true);
      setError(null);
      const token = localStorage.getItem("token");
      
      try {
        // Primero obtener todas las suscripciones de una sola vez si tu API lo permite
        const allSubscriptionsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/userSubscriptions`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          }
        );

        const allSubscriptions = allSubscriptionsResponse.data;
        const now = new Date();
        const subscriptionsData = {};

        // Procesar suscripciones para cada usuario
        users.forEach(user => {
          const userSubs = allSubscriptions.filter(sub => sub.userId === user.id);
          
          // Encontrar suscripción activa
          const activeSub = userSubs.find(sub => 
            sub.status === "active" && 
            new Date(sub.startDate) <= now && 
            new Date(sub.endDate) > now
          );

          if (activeSub) {
            // Buscar detalles del plan en los que ya tenemos
            const planDetails = subscriptionPlans.find(
              plan => plan.id === activeSub.subscriptionPlanId
            );
            
            subscriptionsData[user.id] = {
              subscription: activeSub,
              plan: planDetails || { name: "Plan no disponible" }
            };
          }
        });

        setUserSubscriptions(subscriptionsData);
      } catch (error) {
        console.error("Error al obtener suscripciones:", error);
        setError("No se pudieron cargar las suscripciones");
      } finally {
        setLoadingSubscriptions(false);
        setLoading(false);
      }
    };
    
    fetchAllUserSubscriptions();
  }, [users, subscriptionPlans]);

  const handleOpenModal = (user = null) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };

  const handleDelete = (user) => {
    if (window.confirm(`¿Estás seguro de eliminar al usuario ${user.name}?`)) {
      setIsDeleting(true);
      dispatch(deleteUser(user.id))
        .unwrap()
        .then(() => {
          alert("Usuario eliminado correctamente");
          dispatch(fetchUsers({ page: currentPage, limit: itemsPerPage }));
        })
        .catch((error) => {
          alert(error || "Error al eliminar usuario");
        })
        .finally(() => {
          setIsDeleting(false);
        });
    }
  };

  // Funciones para la paginación
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold bg-white bg-clip-text text-transparent">
          Gestión de Usuarios
        </h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-900/50 text-red-300 rounded-lg border border-red-700">
          {error}
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Plan Activo
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800/50 divide-y divide-gray-700">
              {usersLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-300">
                    <div className="flex justify-center items-center">
                      <div className="h-6 w-6 mr-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin"></div>
                      Cargando usuarios...
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-300">
                    No se encontraron usuarios
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-700/50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-700 flex items-center justify-center">
                          {user.photoURL ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user.photoURL}
                              alt={user.name}
                            />
                          ) : (
                            <UserIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {user.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          user.role === "admin"
                            ? "bg-green-900/50 text-green-400 border border-green-400/30"
                            : "bg-blue-900/50 text-blue-400 border border-blue-400/30"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {loadingSubscriptions ? (
                        <div className="flex items-center">
                          <div className="h-4 w-4 mr-2 rounded-full border-2 border-yellow-400 border-t-transparent animate-spin"></div>
                          <span className="text-gray-400 text-sm">Cargando...</span>
                        </div>
                      ) : userSubscriptions[user.id] ? (
                        <div>
                          <div className="flex items-center mb-1">
                            <Crown className="h-4 w-4 text-yellow-400 mr-1" />
                            <span className="text-white text-sm font-medium">
                              {userSubscriptions[user.id].plan.name}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <CheckCircle2 className="h-3 w-3 text-green-400 mr-1" />
                            <span className="text-xs text-gray-300">
                              Vence: {new Date(userSubscriptions[user.id].subscription.endDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-400 text-sm">
                          <AlertCircle className="h-4 w-4 mr-1 text-gray-500" />
                          <span>Sin plan activo</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleOpenModal(user)}
                          className="text-blue-400 hover:text-blue-300 transition-colors duration-200 flex items-center gap-2"
                        >
                          <Edit2 className="h-4 w-4" />
                          <span>Editar</span>
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          disabled={isDeleting}
                          className="text-red-400 hover:text-red-300 transition-colors duration-200 flex items-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>{isDeleting ? 'Eliminando...' : 'Eliminar'}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación */}
      {!usersLoading && !error && users.length > 0 && (
        <div className="px-4 py-3 bg-gray-800 border-t border-gray-700 sm:px-6 flex justify-center mt-4">
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-700 text-sm font-medium 
                  ${currentPage === 1 
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              >
                <span className="sr-only">Anterior</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Números de página */}
              {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                // Cálculo para mostrar las páginas correctas cuando hay muchas
                let pageNumber;
                
                if (totalPages <= 5) {
                  // Si hay 5 o menos páginas, mostrar todas
                  pageNumber = index + 1;
                } else {
                  // Si hay más de 5 páginas, mostrar de forma inteligente
                  if (currentPage <= 3) {
                    pageNumber = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + index;
                  } else {
                    pageNumber = currentPage - 2 + index;
                  }
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium
                      ${currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
              
              <button
                onClick={goToNextPage}
                disabled={currentPage >= totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-700 text-sm font-medium 
                  ${currentPage === totalPages 
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              >
                <span className="sr-only">Siguiente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      )}

      <AddEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        data={selectedUser}
        type="user"
      />
    </div>
  );
};

export default UsersList;